import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Image,
  Link,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaArrowRight, FaBars } from "react-icons/fa6";

const buildNavLinks = (pathname) => [
  { label: "About", to: "/about-us", active: pathname === "/about-us" },
  { label: "Contact", to: "/contact-us", active: pathname === "/contact-us" },
  { label: "Login", to: "/signin", active: pathname === "/signin" },
];

const NavLinkPill = ({ label, href, to, icon, active }) => {
  const linkProps = href ? { as: "a", href } : { as: RouterLink, to };
  return (
    <Link
      {...linkProps}
      display="inline-flex"
      alignItems="center"
      gap="6px"
      color={active ? "white" : "rgba(230,236,245,0.78)"}
      bg={active ? "rgba(255,255,255,0.10)" : "transparent"}
      border={active ? "1px solid rgba(255,255,255,0.10)" : "1px solid transparent"}
      fontSize="13px"
      fontWeight={500}
      px="14px"
      py="7px"
      borderRadius="9999px"
      transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{ color: "white", bg: "rgba(255,255,255,0.06)", textDecoration: "none" }}
    >
      {icon}
      {label}
    </Link>
  );
};

const LandingNavbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navLinks = buildNavLinks(location.pathname);

  return (
    <Box position="sticky" top={0} zIndex={50} py="16px">
      <Flex
        mx="auto"
        maxW="1240px"
        align="center"
        gap="14px"
        bg="rgba(15, 22, 38, 0.55)"
        border="1px solid rgba(255,255,255,0.08)"
        borderRadius="9999px"
        backdropFilter="blur(18px) saturate(140%)"
        boxShadow="0 18px 40px -16px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)"
        pl="18px"
        pr="14px"
        py="10px"
      >
        <Link
          as={RouterLink}
          to="/"
          display="flex"
          alignItems="center"
          pr="12px"
          borderRight="1px solid rgba(255,255,255,0.10)"
          _hover={{ textDecoration: "none" }}
        >
          <Image
            src="/spearbi_ai4.png"
            alt="SpearBI"
            h="80px"
            objectFit="contain"
          />
        </Link>

        <HStack spacing="4px" ml="8px" display={{ base: "none", lg: "flex" }}>
          {navLinks.map((link) => (
            <NavLinkPill key={link.label} {...link} />
          ))}
        </HStack>

        <HStack ml="auto" spacing="8px" align="center">
          <HStack
            spacing="6px"
            pr="8px"
            fontSize="11px"
            color="rgba(230,236,245,0.55)"
            display={{ base: "none", md: "flex" }}
          >
            <Box
              className="lp-pulse-dot"
              w="8px"
              h="8px"
              borderRadius="50%"
              bg="#14b8a6"
              boxShadow="0 0 0 4px rgba(20,184,166,0.18)"
              sx={{ animation: "lp-pulse 2.2s infinite" }}
            />
            <Text>All systems operational</Text>
          </HStack>

          <Button
            as={RouterLink}
            to="/signin"
            variant="unstyled"
            display={{ base: "none", sm: "inline-flex" }}
            alignItems="center"
            gap="8px"
            bg="transparent"
            color="rgba(230,236,245,0.85)"
            border="1px solid rgba(255,255,255,0.14)"
            borderRadius="9999px"
            fontSize="13px"
            fontWeight={600}
            px="18px"
            py="9px"
            h="auto"
            transition="all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)"
            _hover={{
              bg: "rgba(255,255,255,0.06)",
              color: "white",
              transform: "translateY(-1px)",
            }}
          >
            Sign in
          </Button>

          <Button
            as={RouterLink}
            to="/contact-us"
            variant="unstyled"
            display="inline-flex"
            alignItems="center"
            gap="8px"
            color="white"
            bg="linear-gradient(135deg, #04aec1 0%, #6366f1 60%, #ec4899 120%)"
            backgroundSize="200% 200%"
            backgroundPosition="0% 50%"
            borderRadius="9999px"
            fontSize="13px"
            fontWeight={600}
            px="18px"
            py="9px"
            h="auto"
            boxShadow="0 10px 24px -8px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.25)"
            transition="all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)"
            _hover={{
              transform: "translateY(-2px)",
              backgroundPosition: "100% 50%",
              boxShadow:
                "0 14px 32px -10px rgba(236,72,153,0.55), inset 0 1px 0 rgba(255,255,255,0.3)",
            }}
          >
            Book a demo
            <FaArrowRight size={11} />
          </Button>

          <IconButton
            aria-label="Open menu"
            icon={<FaBars />}
            display={{ base: "inline-flex", lg: "none" }}
            variant="unstyled"
            color="white"
            onClick={onOpen}
            minW="auto"
          />
        </HStack>
      </Flex>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="#0a0e1a" color="white">
          <DrawerCloseButton />
          <DrawerBody pt={16}>
            <Flex direction="column" gap={4}>
              {navLinks.map((link) => {
                const linkProps = link.href
                  ? { as: "a", href: link.href }
                  : { as: RouterLink, to: link.to };
                return (
                  <Link
                    key={link.label}
                    {...linkProps}
                    onClick={onClose}
                    fontSize="lg"
                    color="rgba(230,236,245,0.85)"
                    _hover={{ color: "white", textDecoration: "none" }}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default LandingNavbar;
